import moment from 'moment'
import {
  getWeekdaysInWeekForMonth,
  getNumberOfWeekdaysInMonth,
} from '../../Approvals/Approvals.utils'
import { shortMonth } from '../mocks/wipAiFixture'

/** Months string for WIP query: current month ± range so prior months exist for history sparks. */
export function buildMonthsQueryString(now = moment()) {
  const set = new Set()
  for (let i = -4; i <= 12; i++) {
    set.add(
      now
        .clone()
        .add(i, 'months')
        .format('YYYY-MM'),
    )
  }
  return Array.from(set)
    .sort()
    .join(',')
}

function pacingDelta(pacingWeek, isoWeek) {
  if (!pacingWeek || isoWeek == null) return 0
  const n = Number(isoWeek)
  if (pacingWeek[n] !== undefined && pacingWeek[n] !== null) return Number(pacingWeek[n]) || 0
  const s = String(n)
  if (pacingWeek[s] !== undefined && pacingWeek[s] !== null) return Number(pacingWeek[s]) || 0
  return 0
}

/** Sum of weekly pacing deltas (USD) for the month — funnel "actual minus expected" per ISO week. */
export function monthPacingUsd(wipReport) {
  if (!wipReport?.pacingWeek) return 0
  return Object.values(wipReport.pacingWeek).reduce((sum, v) => sum + (Number(v) || 0), 0)
}

export function deriveTrend(wipReport) {
  if (!wipReport) return 'On Target'
  const pct = parseFloat(String(wipReport.wipPacing || '0').replace('%', '')) || 0
  const budget = Number(wipReport.budget) || 0
  const actual = Number(wipReport.totalFeesAndExpenses) || 0
  const variance = actual - budget
  if (pct > 110 || (budget > 0 && variance > budget * 0.1)) return 'Over Budget'
  if (pct > 102) return 'Ahead'
  if (pct < 95) return 'Behind'
  return 'On Target'
}

/**
 * @param {object} wipReport
 * @param {string} reportMonthStr - YYYY-MM row being viewed
 * @param {string} calendarTodayStr - actual calendar date YYYY-MM-DD
 */
export function computeForecastEom(
  wipReport,
  reportMonthStr,
  calendarTodayStr = moment().format('YYYY-MM-DD'),
) {
  if (!wipReport || !reportMonthStr) return 0
  const actual = Number(wipReport.totalFeesAndExpenses) || 0
  const reportStart = moment(reportMonthStr, 'YYYY-MM', true)
  if (!reportStart.isValid()) return actual
  const today = moment(calendarTodayStr, 'YYYY-MM-DD', true).isValid()
    ? moment(calendarTodayStr)
    : moment()

  if (reportStart.isBefore(today, 'month')) {
    return actual
  }
  if (reportStart.isAfter(today, 'month')) {
    return actual
  }
  const dom = today.date()
  const dim = today
    .clone()
    .endOf('month')
    .date()
  if (!dom || !dim) return actual
  return actual * (dim / dom)
}

export function buildHistorySparks(monthlyWipReports, currentMonth) {
  const all = monthlyWipReports || []
  const idx = all.findIndex(m => m?.month === currentMonth)
  if (idx < 0) return []
  return all.slice(Math.max(0, idx - 3), idx).map(m => {
    const sm = shortMonth(m.month)
    const mLabel = sm.replace(/\s\d{4}/, '').toUpperCase()
    return {
      mLabel,
      total: m.totalFeesAndExpenses,
      budget: m.budget,
      pacing: Math.round(parseFloat(String(m.wipPacing || '0').replace('%', '')) || 0),
      sparks: [0.7, 0.9, 1.05, 1.0].map(x => (x * (Number(m.totalFeesAndExpenses) || 0)) / 4),
    }
  })
}

function isoWeekStartsInMonth(year, monthIndex0) {
  const seen = new Set()
  const out = []
  const dim = moment([year, monthIndex0]).daysInMonth()
  for (let d = 1; d <= dim; d++) {
    const dayMoment = moment([year, monthIndex0, d])
    const wk = dayMoment.isoWeek()
    const wy = dayMoment.isoWeekYear()
    const key = `${wy}-W${wk}`
    if (!seen.has(key)) {
      seen.add(key)
      out.push({ weekStart: dayMoment.clone().startOf('isoWeek'), isoWeek: wk })
    }
  }
  return out
}

function weekRangeLabel(weekStart, year, monthIndex0) {
  const endOfWeek = weekStart.clone().endOf('isoWeek')
  const monthStart = moment([year, monthIndex0]).startOf('month')
  const monthEnd = moment([year, monthIndex0]).endOf('month')
  const a = moment.max(weekStart, monthStart)
  const b = moment.min(endOfWeek, monthEnd)
  return `${a.format('MMM D')}–${b.format('D')}`
}

/**
 * Weekly target/actual in USD from monthly budget proration + pacingWeek deltas.
 * @param {object} wipReport - monthly WIP row with budget, pacingWeek
 * @param {string} monthStr - YYYY-MM
 */
export function buildWeeklyPacing(wipReport, monthStr) {
  if (!wipReport || !monthStr) return []
  const parts = monthStr.split('-').map(Number)
  const y = parts[0]
  const m0 = parts[1] - 1
  if (!y || m0 < 0 || m0 > 11) return []

  const monthBudget = Number(wipReport.budget) || 0
  const totalWeekdays = getNumberOfWeekdaysInMonth(y, m0)
  if (totalWeekdays <= 0) return []

  const pacingWeek = wipReport.pacingWeek || {}
  const weeks = isoWeekStartsInMonth(y, m0)

  return weeks.map(({ weekStart, isoWeek }) => {
    const weekdaysInWeek = getWeekdaysInWeekForMonth(weekStart, y, m0)
    const target = monthBudget * (weekdaysInWeek / totalWeekdays)
    const delta = pacingDelta(pacingWeek, isoWeek)
    const actual = target + delta
    return {
      week: weekRangeLabel(weekStart, y, m0),
      target,
      actual,
    }
  })
}

export function reportingPeriodLabel(monthStr, calendarTodayStr = moment().format('YYYY-MM-DD')) {
  if (!monthStr) return ''
  const parts = monthStr.split('-').map(Number)
  const y = parts[0]
  const m0 = parts[1] - 1
  if (!y || m0 < 0 || m0 > 11) return ''
  const start = moment([y, m0, 1])
  const monthEnd = moment([y, m0]).endOf('month')
  const today = moment(calendarTodayStr, 'YYYY-MM-DD', true).isValid()
    ? moment(calendarTodayStr)
    : moment()
  const sameMonthAsToday = today.year() === y && today.month() === m0
  const end = sameMonthAsToday ? moment.min(today, monthEnd) : monthEnd
  return `${start.format('MMM DD')} – ${end.format('MMM DD, YYYY')}`
}
